/* eslint-disable @nx/enforce-module-boundaries */
'use client';

import { FormattedInput, If } from '@payments-checkout/shared/components';
import { isDefaultBrand } from '@payments-checkout/shared/helpers/constants/brand';
import {
  InstallmentsManager,
  MathManager,
  StringManager
} from '@payments-checkout/shared/helpers/index';
import { isAmexBrand } from '@payments-checkout/shared/helpers/validators/cvv-validator';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { Alert, MaskedInput, Select, Text } from '@stone-payments/jade';
import { useRecurrenceValues } from 'modules/checkout/src/app/hook/useRecurrence/useRecurrenceValues.hook';
import { useDebounce } from 'modules/checkout/src/hooks/useDebounce';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useFormTypedValues } from '../../../app/providers/formValuesProvider.provider';
import { CreditCardFormFields } from '../../../app/types/billingInformation.types';
import { useBin } from '../../../hooks/useBin';
import { usePageSharedData } from '../../../hooks/usePageSharedData';
import { GlobalWalletSwitch } from './globalWalletSwitch';

const buildTrailingItemProp = (brand?: string) => {  
if (!brand || isDefaultBrand(brand)) return undefined;
  return {
    type: 'brand',
    content: brand,
  };
};

interface CreditCardFormProps {
  disabled?: boolean;
}

export const CreditCardForm = ({ disabled = false }: CreditCardFormProps) => {
  const { formatMessage } = useIntl();
  const {
    installments,
    brand,
    setInstallments,
    setInstallmentsExplanationText,
    setBrand,
    setSurchargesText,
    threedSecureEnabled,
  } = usePaymentMethod();
  const { installmentOptions, isDeliveryEnabled } = usePageSharedData();
  const { installmentsStoneDelivery } = useFormTypedValues();
  const { isRecurrence } = useRecurrenceValues();

  const deliveryInstallmentOptions = useMemo(() => {
    if (!isDeliveryEnabled || !installmentsStoneDelivery) return [];

    const matchingBrandPlan =
      installmentsStoneDelivery.find(
        (entry) => entry.brand.toLowerCase() === (brand || '').toLowerCase()
      ) ??
      installmentsStoneDelivery.find(
        (entry) => entry.brand.toLowerCase() === 'default'
      );

    if (!matchingBrandPlan) return [];

    return matchingBrandPlan.installments.map(({ number, total }) => ({
      label: `${number}x de ${MathManager.formatCurrency({ value: (total / number / 100).toFixed(2) })}`,
      value: number.toString(),
      total: matchingBrandPlan.installments[0]?.total ?? 0,
      totalWithSurcharges: total,
      useRate: false,
    }));
  }, [isDeliveryEnabled, installmentsStoneDelivery, brand]);

  const installmentOptionsToUse = isDeliveryEnabled ? deliveryInstallmentOptions : installmentOptions;

  const formattedInstallmentsOptions = InstallmentsManager.formatOptions(
    installmentOptionsToUse,
    formatMessage
  );

  const {
    register,
    control,
    watch,
    trigger,
    formState: { errors, touchedFields },
    setValue,
  } = useFormContext<CreditCardFormFields>();

  const [cardNumber, setCardNumber] = useState('0');
  const debouncedCardNumber = useDebounce(cardNumber, 1000);
  const { refetch } = useBin(StringManager.onlyNumbers(cardNumber));

  const creditCardValue = watch('creditCard');

  const handleCreditCard = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.value.length === 0) setBrand('DEFAULT');

    const value = StringManager.onlyNumbers(e.currentTarget.value || '0');

    const sixFirstCardNumber = value.length > 5 ? value.slice(0, 6) : value;

    setCardNumber(sixFirstCardNumber);
  };

  const handleInstallments = (e: React.ChangeEvent<HTMLSelectElement>) =>
    InstallmentsManager.handleInstallmentsChange(
      e.target.value,
      formattedInstallmentsOptions,
      setInstallments,
      setInstallmentsExplanationText,
      setSurchargesText
    );

  const isInstallmentsDisabled =
    disabled ||
    !creditCardValue ||
    formattedInstallmentsOptions.length === 1;

  const brandProps = creditCardValue && buildTrailingItemProp(brand);

  useEffect(() => {
    if (isRecurrence) {
      setValue('installments', '1');
    }
  }, []);

  useEffect(() => {
    if (formattedInstallmentsOptions.length === 1) {
      const [onlyOption] = formattedInstallmentsOptions;
      setValue('installments', onlyOption.value);
      InstallmentsManager.handleInstallmentsChange(
        onlyOption.value,
        formattedInstallmentsOptions,
        setInstallments,
        setInstallmentsExplanationText,
        setSurchargesText
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedInstallmentsOptions]);

  useEffect(() => {
    if (Number(debouncedCardNumber) > 0) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCardNumber, refetch]);

  useEffect(() => {
    if (touchedFields.cvv) trigger('cvv');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand]);

  const cvvMaxLength = isAmexBrand(brand) ? 4 : isDefaultBrand(brand) ? 4 : 3;

  const trackCardFieldFilled =
    (reference: string) => (event: { target: { value: string } }) => {
      if (!event.target.value.trim()) return;

      analiticaInstance.events.billingPage.cardFieldFilled(reference);
    };

  return (
    <div
      data-testid="credit-card-container"
      className="flex flex-col gap-jade-150 bg-jade-background-surface rounded-xl p-jade-250 min-500:p-0 min-500:rounded-none"
    >
      <Text variant="heading-small">
        {formatMessage({ id: 'creditCardForm.title' })}
      </Text>
      <div className="flex flex-col flex-wrap gap-jade-200 rounded-jade-medium bg-jade-background-surface min-500:bg-jade-background-neutral-subtle min-500:p-jade-250">
        <Controller
          name="creditCard"
          control={control}
          render={({ field }) => (
            <MaskedInput
              {...field}
              mask="cardNumber"
              size="medium"
              type="text"
              inputMode="numeric"
              disabled={disabled}
              label={formatMessage({ id: 'creditCardForm.cardNumber' })}
              placeholder="0000 0000 0000 0000"
              leadingItem={{
                type: 'icon',
                content: 'card',
              }}
              trailingItem={brandProps as any}
              error={!!errors.creditCard?.message}
              supportText={
                errors?.creditCard?.message &&
                formatMessage({
                  id: `creditCardFields.validate.${errors?.creditCard?.message}`,
                })
              }
              onChange={(e) => {
                field.onChange(e);
                handleCreditCard(e);
              }}
              onBlur={(e) => {
                field.onBlur();
                trackCardFieldFilled('campo_numero_cartao')(e);
              }}
            />
          )}
        />

        <FormattedInput
          label={formatMessage({ id: 'creditCardForm.cardHolder' })}
          placeholder={formatMessage({
            id: 'creditCardForm.cardHolder.placeholder',
          })}
          size="medium"
          disabled={disabled}
          error={!!errors?.cardHolder?.message}
          supportText={
            errors?.cardHolder?.message &&
            formatMessage({
              id: `creditCardFields.validate.${errors?.cardHolder?.message}`,
            })
          }
          formatter="cardHolder"
          {...register('cardHolder', {
            onBlur: trackCardFieldFilled('campo_nome_cartao'),
          })}
        />

        <div className="flex gap-jade-200">
          <FormattedInput
            label={formatMessage({ id: 'creditCardForm.expirationDate' })}
            placeholder={formatMessage({
              id: 'creditCardForm.expirationDate.placeholder',
            })}
            size="medium"
            type="text"
            inputMode="numeric"
            style={{ width: '50%' }}
            disabled={disabled}
            formatter="date"
            error={!!errors?.expirationDate?.message}
            supportText={
              errors?.expirationDate?.message &&
              formatMessage({
                id: `creditCardFields.validate.${errors?.expirationDate?.message}`,
              })
            }
            {...register('expirationDate', {
              onBlur: trackCardFieldFilled('campo_vencimento_cartao'),
            })}
          />
          <FormattedInput
            formatter="cvv"
            maxLength={cvvMaxLength}
            label="CVV *"
            placeholder={isAmexBrand(brand) ? '0000' : '000'}
            leadingItem={{
              type: 'icon',
              content: 'lock-close',
            }}
            size="medium"
            type="text"
            inputMode="numeric"
            style={{ width: '50%' }}
            disabled={disabled}
            error={!!errors?.cvv?.message}
            supportText={
              errors?.cvv?.message &&
              formatMessage({
                id: `creditCardFields.validate.${errors?.cvv?.message}`,
              })
            }
            {...register('cvv', {
              onBlur: trackCardFieldFilled('campo_cvv_cartao'),
            })}
          />
        </div>
        <If condition={!isRecurrence}>
          <Controller
            name="installments"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                size="medium"
                label={formatMessage({ id: 'creditCardForm.installments' })}
                placeholder={formatMessage({
                  id: 'creditCardForm.installmentsSelect.placeholder',
                })}
                options={formattedInstallmentsOptions}
                defaultValue={installments?.toString()}
                error={!!errors?.installments?.message}
                supportText={
                  errors?.installments?.message &&
                  formatMessage({
                    id: `creditCardFields.validate.${errors?.installments?.message}`,
                  })
                }
                onChange={(e) => {
                  field.onChange(e);
                  handleInstallments(e);
                  analiticaInstance.events.billingPage.installmentsSelected(
                    e.target.value
                  );
                }}
                disabled={isInstallmentsDisabled}
                value={installments || field.value}
              />
            )}
          />
        </If>
      </div>
      <GlobalWalletSwitch />
      {threedSecureEnabled && (
        <div className="mt-jade-300">
          <Alert
            variant="info"
            showIcon
            description={formatMessage({ id: 'customerForm.tdsInfo' })}
          />
        </div>
      )}
    </div>
  );
};
